import { ApiError } from "./errors";
import { getAccessToken } from "./tokens";

/** A downloaded file: its bytes and the server's filename (Content-Disposition), if any. */
export interface DownloadedFile {
  blob: Blob;
  filename: string | null;
}

function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) return decodeURIComponent(encoded[1]);
  return /filename="?([^";]+)"?/i.exec(header)?.[1] ?? null;
}

/** Authenticated request for endpoints that return a file instead of JSON. */
async function fetchFile(path: string, init: RequestInit = {}): Promise<DownloadedFile> {
  const token = await getAccessToken();
  const response = await fetch(`${globalThis.location.origin}/api/v1${path}`, {
    ...init,
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, response.statusText, body, response.headers.get("x-request-id"));
  }
  return { blob: await response.blob(), filename: filenameFrom(response.headers.get("content-disposition")) };
}

export type ChatExportFormat = "md" | "docx" | "pdf";

/** Export a chat as a document (`POST /chat/sessions/{uuid}/export`). */
export function exportChat(sessionUuid: string, format: ChatExportFormat): Promise<DownloadedFile> {
  return fetchFile(`/chat/sessions/${encodeURIComponent(sessionUuid)}/export`, {
    method: "POST",
    body: JSON.stringify({ format, template: "clean", content: "full", include_system_messages: false }),
  });
}

/** A chat attachment's original file. */
export function fetchChatAttachment(attachmentUuid: string): Promise<DownloadedFile> {
  return fetchFile(`/chat/attachments/${encodeURIComponent(attachmentUuid)}/file`);
}

/**
 * Whether a URL in assistant output is a backend path that needs an authenticated fetch.
 * The model writes these relative to the API (`/chat/attachments/{uuid}/file`), not the app.
 */
export function isApiFilePath(url: string): boolean {
  return url.startsWith("/chat/") || url.startsWith("/api/v1/");
}

/** Fetch a backend path from assistant output (see `isApiFilePath`). */
export function fetchApiFile(path: string): Promise<DownloadedFile> {
  return fetchFile(path.startsWith("/api/v1/") ? path.slice("/api/v1".length) : path);
}

/** A PDF rendering of a chat document (Office files are converted server-side). */
export function fetchChatAttachmentPreview(attachmentUuid: string): Promise<DownloadedFile> {
  return fetchFile(`/chat/attachments/${encodeURIComponent(attachmentUuid)}/preview`);
}

function dataSourceFilePath(collectionUuid: string, fileUuid: string): string {
  return `/data-sources/${encodeURIComponent(collectionUuid)}/files/${encodeURIComponent(fileUuid)}`;
}

/** A data-source file's original content. */
export function fetchDataSourceFile(collectionUuid: string, fileUuid: string): Promise<DownloadedFile> {
  return fetchFile(`${dataSourceFilePath(collectionUuid, fileUuid)}/stream`);
}

/** A data-source file's extracted, AI-ready markdown. */
export async function fetchFileMarkdown(collectionUuid: string, fileUuid: string): Promise<string> {
  return (await fetchFile(`${dataSourceFilePath(collectionUuid, fileUuid)}/markdown`)).blob.text();
}

/** A PDF rendering of a data-source file (Office files are converted server-side). */
export function fetchDataSourceFilePreview(collectionUuid: string, fileUuid: string): Promise<DownloadedFile> {
  return fetchFile(`${dataSourceFilePath(collectionUuid, fileUuid)}/preview`);
}

function spaceFilePath(spaceUuid: string, fileUuid: string): string {
  return `/spaces/${encodeURIComponent(spaceUuid)}/files/${encodeURIComponent(fileUuid)}`;
}

/** A space knowledge-base file's original content. */
export function fetchSpaceFile(spaceUuid: string, fileUuid: string): Promise<DownloadedFile> {
  return fetchFile(`${spaceFilePath(spaceUuid, fileUuid)}/file`);
}

/** A PDF rendering of a space knowledge-base file (Office files are converted server-side). */
export function fetchSpaceFilePreview(spaceUuid: string, fileUuid: string): Promise<DownloadedFile> {
  return fetchFile(`${spaceFilePath(spaceUuid, fileUuid)}/preview`);
}

/** A space knowledge-base file's extracted, AI-ready markdown. */
export async function fetchSpaceFileMarkdown(spaceUuid: string, fileUuid: string): Promise<string> {
  return (await fetchFile(`${spaceFilePath(spaceUuid, fileUuid)}/markdown`)).blob.text();
}
